import { ref, computed, watch } from 'vue';
import { useMainStore } from '../stores/mainStore';
import { useRoute, navigateTo, useNuxtApp, createError, useAsyncData } from '#imports';
import { extractUrlParams } from '../helpers/pagination';

export function useDocumentsData(folderId = null) {
  const mainStore = useMainStore();
  const route = useRoute();
  const { $t, $axios } = useNuxtApp();

  const pathPrefix = computed(() => mainStore.pathPrefix);
  const sectionName = computed(() => mainStore.sectionName);

  const isFetching = ref(false);

  const asyncDataKey = folderId ? `document-folder-id-${folderId}` : 'documents-root';

  const { data: documentsData } = useAsyncData(asyncDataKey, async () => {
    isFetching.value = true;
    try {
      const PAGE_SIZE = 40;

      const { url, params: apiParams } = mainStore.getDocumentsApi($axios.defaults.baseURL);

      const data = await fetchDocumentsData(url, apiParams, folderId, PAGE_SIZE);

      return data;
    } catch (e) {
      handleError(e);
    } finally {
      isFetching.value = false;
    }
  });

  const foldersAndDocuments = computed(() => documentsData.value?.foldersAndDocuments || []);
  const folderBreadcrumbs = computed(() => documentsData.value?.folderBreadcrumbs || []);
  const routePath = computed(() => documentsData.value?.routePath || []);

  if (folderId) {
    watch(
      () => documentsData.value,
      (newData) => {
        if (newData && newData.routePath) {
          const expectedPath = `${pathPrefix.value}/documents/${newData.routePath.join('/')}/${folderId}`;
          if (route.path !== expectedPath && newData.routePath.length) {
            navigateTo({ path: expectedPath });
          }
        }
      },
      { immediate: true },
    );
  }

  async function fetchDocumentsData(url, apiParams, folderId) {
    const PAGE_SIZE = 40;

    const params = {
      ...apiParams,
      page_size: PAGE_SIZE,
    };

    if (folderId) {
      params.folder_id = folderId;
    }

    const resFirstPage = await $axios.get(url, {
      params,
      cache: { ignoreCache: false },
    });

    const {
      meta: { links, folders, path, current_folder: currentFolder },
    } = resFirstPage.data;

    const hasNewFormat = !!resFirstPage.data.items;
    let totalDocuments = hasNewFormat ? resFirstPage.data.items : resFirstPage.data.documents;

    const { params: lastPageParams } = extractUrlParams(links.last);
    const totalPages = +lastPageParams.page_no;

    const fetchPagePromises = [];
    for (let i = 2; i <= totalPages; i += 1) {
      const pageParams = {
        ...params,
        page_no: i,
      };
      fetchPagePromises.push(
        $axios.get(url, {
          params: pageParams,
          cache: { ignoreCache: false },
        }),
      );
    }

    const additionalPages = await Promise.all(fetchPagePromises);

    additionalPages.forEach(({ data: additionalData }) => {
      if (hasNewFormat) {
        additionalData.items = additionalData.items.filter((item) => !item.folder_name);
      }
      totalDocuments = [...totalDocuments, ...(hasNewFormat ? additionalData.items : additionalData.documents)];
    });

    const routePath = [];
    let folderBreadcrumbs = [];

    if (folderId) {
      const breadcrumbsData = buildBreadcrumbs(path, currentFolder);
      folderBreadcrumbs = breadcrumbsData.folderBreadcrumbs;
      routePath.push(...breadcrumbsData.routePath);
    }

    const foldersAndDocuments = formatItems(totalDocuments, folders, routePath, hasNewFormat);

    return {
      foldersAndDocuments,
      folderBreadcrumbs,
      routePath,
    };
  }

  function buildBreadcrumbs(path, currentFolder) {
    const routePath = [];
    const crumbs = path.map((routeCrumb) => {
      routePath.push(encodeURIComponent(routeCrumb.folder_name.trim().toLowerCase().replace(/\s/g, '-')));
      return {
        name: routeCrumb.folder_name,
        link: `${pathPrefix.value}/documents/${routePath.join('/')}/${routeCrumb.id}`,
      };
    });
    routePath.push(encodeURIComponent(currentFolder.trim().toLowerCase().replace(/\s/g, '-')));

    const folderBreadcrumbs = [
      {
        name: sectionName.value,
        link: `${pathPrefix.value}/documents`,
      },
      ...crumbs,
      {
        name: currentFolder,
        link: '',
      },
    ];

    return { folderBreadcrumbs, routePath };
  }

  function formatItems(items, folders, routePath, hasNewFormat) {
    const formattedItems = (hasNewFormat ? items : folders).map((item) => {
      if (hasNewFormat && !item.folder_name) {
        return item;
      }

      const routePathUrl = routePath.length ? `${routePath.join('/')}/` : '';
      const folderUrl = `${pathPrefix.value}/documents/${routePathUrl}${encodeURIComponent(
        item.folder_name.trim().toLowerCase().replace(/\s/g, '-'),
      )}/${item.id}`;

      return {
        ...item,
        isFolder: true,
        url: folderUrl,
      };
    });

    let foldersAndDocuments;
    if (hasNewFormat) {
      // Manually sort until pagination issue is fixed on the backend
      foldersAndDocuments = [...formattedItems].sort((a, b) => a.position - b.position);
    } else {
      foldersAndDocuments = [...formattedItems, ...items];
    }

    return foldersAndDocuments;
  }

  function handleError(e) {
    const message = e.response?.status === 403 ? $t('intranet.noFolderAccess') : '';
    throw createError({ statusCode: e.response?.status || 500, message });
  }

  return {
    isFetching,
    foldersAndDocuments,
    folderBreadcrumbs,
    routePath,
  };
}
